import { useEffect, useRef, useState } from 'react';

const SOUNDBARS = 24;

function SoundVisualizer() {
  return (
    <div className="flex items-end justify-center gap-1 h-24 md:h-32">
      {Array.from({ length: SOUNDBARS }).map((_, i) => (
        <div
          key={i}
          className="w-1.5 md:w-2 bg-white rounded-full"
          style={{
            height: '20%',
            animation: `soundBar ${1.5 + Math.random() * 1.5}s ease-in-out infinite`,
            animationDelay: `${Math.random() * 2}s`,
            opacity: 0.3 + Math.random() * 0.5,
          }}
        />
      ))}
    </div>
  );
}

export default function HeroSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [lessonMode, setLessonMode] = useState<'offline' | 'online'>('offline');

  useEffect(() => {
    setVisible(true);
  }, []);

  const handleScrollDown = () => {
    const el = document.querySelector('#lessons');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      ref={sectionRef}
      className="relative w-full min-h-screen bg-studio-black overflow-hidden flex items-center justify-center"
    >
      {/* Background image with dark overlay */}
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=A%20professional%20music%20recording%20studio%20interior%20with%20dark%20moody%20atmosphere%2C%20mixing%20console%20with%20glowing%20meters%2C%20studio%20monitors%2C%20acoustic%20panels%20on%20walls%2C%20dramatic%20low-key%20lighting%20in%20grayscale%20tones%2C%20cinematic%20composition%2C%20high-end%20studio%20equipment%2C%20minimal%20and%20sleek%20design%2C%20black%20and%20gray%20color%20palette%2C%20no%20people%2C%20wide%20shot&width=1920&height=1080&seq=hero_bg_1&orientation=landscape"
          alt="Studio Background"
          className="w-full h-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-studio-black/60 via-studio-black/40 to-studio-black" />
      </div>

      {/* Floating particles / dots */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {Array.from({ length: 20 }).map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-white rounded-full animate-pulse-glow"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 5}s`,
              opacity: 0.2 + Math.random() * 0.3,
            }}
          />
        ))}
      </div>

      {/* Main content */}
      <div className="relative z-10 w-full px-6 md:px-12 lg:px-16 text-center">
        <div
          className={`transition-all duration-1000 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          {/* Sound visualizer */}
          <div className="mb-8">
            <SoundVisualizer />
          </div>

          {/* Subtitle */}
          <p className="text-studio-silver font-sans text-sm md:text-base tracking-[0.3em] uppercase mb-6">
            Professional Music Studio
          </p>

          {/* Main headline */}
          <h1
            className="text-white font-bebas uppercase leading-none mb-4"
            style={{ fontSize: 'clamp(64px, 12vw, 180px)', lineHeight: 0.9 }}
          >
            MAKE YOUR
            <br />
            <span className="text-studio-silver">SOUND</span>
          </h1>

          {/* Description */}
          <p className="text-studio-gray font-sans text-base md:text-lg tracking-wide max-w-xl mx-auto mb-8">
            디제잉, 랩, 보컬, 미디 작곡 — 당신만의 음악을 완성하는 곳
          </p>

          {/* Offline / Online Toggle */}
          <div className="flex items-center justify-center mb-10">
            <div className="inline-flex items-center bg-studio-surface/80 border border-studio-border rounded-full p-1 backdrop-blur-sm">
              <button
                type="button"
                onClick={() => setLessonMode('offline')}
                className={`px-6 py-2.5 rounded-full font-sans text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  lessonMode === 'offline'
                    ? 'bg-white text-studio-black'
                    : 'text-studio-silver hover:text-white'
                }`}
              >
                오프라인 레슨
              </button>
              <button
                type="button"
                onClick={() => setLessonMode('online')}
                className={`px-6 py-2.5 rounded-full font-sans text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  lessonMode === 'online'
                    ? 'bg-white text-studio-black'
                    : 'text-studio-silver hover:text-white'
                }`}
              >
                온라인 레슨
              </button>
            </div>
          </div>

          {/* Mode description */}
          <p className="text-studio-gray font-sans text-sm md:text-base mb-10 max-w-lg mx-auto">
            {lessonMode === 'offline'
              ? '파주 야당역 부근 1:1 대면 레슨. 몰입도 높은 학습이 가능합니다.'
              : '언제 어디서나 온라인으로 진행되는 원격 레슨. 고품질 오디오 전송으로 스튜디오와 동일한 수업 경험을 제공합니다.'}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={handleScrollDown}
              className="bg-white text-studio-black font-sans font-bold text-base px-8 py-4 rounded-md hover:bg-studio-light transition-colors cursor-pointer whitespace-nowrap"
            >
              레슨 알아보기
            </button>
            <button
              onClick={() => {
                const el = document.querySelector('#inquiry');
                if (el) el.scrollIntoView({ behavior: 'smooth' });
              }}
              className="border border-studio-muted text-studio-light font-sans font-medium text-base px-8 py-4 rounded-md hover:border-white hover:text-white transition-colors cursor-pointer whitespace-nowrap"
            >
              상담 신청
            </button>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 animate-float">
        <span className="text-studio-gray font-sans text-xs tracking-widest uppercase">Scroll</span>
        <div className="w-px h-8 bg-gradient-to-b from-studio-gray to-transparent" />
      </div>
    </section>
  );
}